/**
 * MIA AI Types and Constants
 * Matches Flutter implementation exactly
 */

// Feature types - matches MiaFeatureType enum in Flutter
export const MiaFeatureType = {
  SUMMARY: "summary",
  FLASH_CARDS: "flashCards",
  QBANK: "qbank",
  PROBLEM_SOLVING: "problemSolving",
};

// Input types - matches MiaInputType enum in Flutter
export const MiaInputType = {
  TEXT: "text",
  IMAGE: "image",
  FILE: "file",
  AUDIO: "audio",
};

// Upload status - matches MiaUploadStatus enum in Flutter
export const MiaUploadStatus = {
  PENDING: "pending",
  UPLOADING: "uploading",
  COMPLETED: "completed",
  FAILED: "failed",
  CANCELLED: "cancelled",
};

// Operation status - matches API status pattern
export const OperationStatus = {
  PENDING: "pending",
  PROCESSING: "processing",
  COMPLETED: "completed",
  FAILED: "failed",
};

// Refresh types - matches MiaRefreshType enum in Flutter
export const MiaRefreshType = {
  SUMMARY: "summary",
  FLASH_CARDS: "flashCards",
  QBANK: "qbank",
  PROBLEM_SOLVING: "problemSolving",
};

// API Endpoints - matches mia_features.md exactly
// Note: apiUrl from @/utils/api already includes '/api', so endpoints should NOT include 'api/' prefix
export const MiaEndpoints = {
  // Operations
  OPERATIONS: "ai/operations",

  // Problem Solving
  PROBLEMS: "ai/problems",
  PROBLEM_STATUS: (problemId, solutionId) =>
    `ai/problems/${problemId}/solutions/${solutionId}/status`,
  PROBLEM_RETRY: (problemId, solutionId) =>
    `ai/problems/${problemId}/solutions/${solutionId}/retry`,

  // Flash Cards
  FLASH_CARD_DECKS: "flash-cards/decks",
  FLASH_CARD_DECK_STATUS: (deckId) => `flash-cards/decks/${deckId}/status`,
  FLASH_CARD_DECK_RETRY: (deckId) => `flash-cards/decks/${deckId}/retry`,
  FLASH_CARD_DECK_STATISTICS: (deckId) =>
    `flash-cards/decks/${deckId}/statistics`,
  FLASH_CARD_STUDY: (deckId) => `flash-cards/decks/${deckId}/study`,
  FLASH_CARD_PROGRESS: (cardId) => `flash-cards/cards/${cardId}/progress`,

  // Question Banks
  QUESTION_BANKS: "qbanks",
  QUESTION_BANKS_FROM_CONTENT: "question-banks/from-content",
  QUESTION_BANK_STATUS: (bankId) => `question-banks/${bankId}/status`,
  QUESTION_BANK_RETRY: (bankId) => `question-banks/${bankId}/retry`,

  // Summaries
  SUMMARIES: "ai/outputs",
  SUMMARY_PROCESS: "ai/process-content",
  SUMMARY_STATUS: (id) => `ai/outputs/${id}/status`,
  SUMMARY_RETRY: (id) => `ai/outputs/${id}/retry`,
};

// Solution types for problem solving
export const SolutionTypes = {
  DIRECT_SOLUTION: "direct_solution",
  STEP_BY_STEP_EXPLANATION: "step_by_step_explanation",
  IN_DEPTH_THEORETICAL_SOLUTION: "in_depth_theoretical_solution",
  SIMILAR_EQUATIONS: "similar_equations",
  COMMON_MISTAKES: "common_mistakes",
};

// Output types for summaries
export const OutputTypes = {
  SUMMARIZE: "summarize",
  REFORMAT: "reformat",
  GRAMMAR_CORRECTION: "grammar_correction",
};

// Summary types
export const SummaryTypes = {
  GENERAL: "general",
  BULLET_POINTS: "bullet_points",
  KEY_POINTS: "key_points",
  DETAILED: "detailed",
  EXECUTIVE: "executive",
};

// Reformat styles
export const ReformatStyles = {
  NOTES: "notes",
  ARTICLE: "article",
  REPORT: "report",
  OUTLINE: "outline",
};

// Languages
export const Languages = {
  AUTO: "auto",
  ARABIC: "ar",
  ENGLISH: "en",
};

// Polling configuration - matches Flutter (3 seconds interval, 3 minutes timeout)
export const PollingConfig = {
  INTERVAL_MS: 3000,
  TIMEOUT_MS: 180000, // 3 minutes
};

// File type extensions
export const FileExtensions = {
  IMAGES: ["jpg", "jpeg", "png", "gif", "bmp", "webp"],
  DOCUMENTS: ["pdf"],
  AUDIO: ["mp3", "wav", "m4a", "ogg"],
};

// Status colors - matches Flutter implementation
export const StatusColors = {
  completed: "#22c55e", // green
  processing: "#3b82f6", // blue
  pending: "#f97316", // orange
  failed: "#ef4444", // red
};

// Feature icons mapping
export const FeatureIcons = {
  [MiaFeatureType.PROBLEM_SOLVING]: "flask",
  [MiaFeatureType.QBANK]: "clipboard-list",
  [MiaFeatureType.SUMMARY]: "file-text",
  [MiaFeatureType.FLASH_CARDS]: "layers",
};

// Input type icons mapping
export const InputTypeIcons = {
  [MiaInputType.TEXT]: "type",
  [MiaInputType.IMAGE]: "image",
  [MiaInputType.FILE]: "file",
  [MiaInputType.AUDIO]: "mic",
};

// Operation type mapping (from API response)
export const OperationTypeMapping = {
  ai_problem: MiaFeatureType.PROBLEM_SOLVING,
  question_bank: MiaFeatureType.QBANK,
  file_processing: MiaFeatureType.SUMMARY,
  flash_card_deck: MiaFeatureType.FLASH_CARDS,
};

// Title max lengths - matches Flutter implementation
export const TitleMaxLengths = {
  PROBLEM: 191,
  FLASH_CARD_DECK: 255,
  QUESTION_BANK: 191,
  SUMMARY: 191,
};

// Default config values
export const DefaultConfig = {
  QUESTION_COUNT: 15,
  CARD_COUNT: 10,
  LANGUAGE: Languages.AUTO,
};

/**
 * Helper function to get status color
 * @param {string} status
 * @returns {string} hex color
 */
export function getStatusColor(status) {
  return StatusColors[status] || StatusColors.pending;
}

/**
 * Helper function to check if file is an image
 * @param {string} filePath
 * @returns {boolean}
 */
export function isImageFile(filePath) {
  if (!filePath) return false;
  const ext = filePath.split(".").pop()?.toLowerCase();
  return FileExtensions.IMAGES.includes(ext);
}

/**
 * Helper function to check if file is audio
 * @param {string} filePath
 * @returns {boolean}
 */
export function isAudioFile(filePath) {
  if (!filePath) return false;
  const ext = filePath.split(".").pop()?.toLowerCase();
  return FileExtensions.AUDIO.includes(ext);
}

/**
 * Helper function to check if file is PDF
 * @param {string} filePath
 * @returns {boolean}
 */
export function isPdfFile(filePath) {
  if (!filePath) return false;
  const ext = filePath.split(".").pop()?.toLowerCase();
  return ext === "pdf";
}

/**
 * Convert MiaFeatureType to MiaRefreshType
 * @param {string} featureType
 * @returns {string}
 */
export function featureToRefreshType(featureType) {
  return featureType; // They use the same values
}

/**
 * Get feature endpoint based on feature type
 * @param {string} featureType
 * @returns {string}
 */
export function getFeatureEndpoint(featureType) {
  switch (featureType) {
    case MiaFeatureType.SUMMARY:
      return MiaEndpoints.SUMMARY_PROCESS;
    case MiaFeatureType.FLASH_CARDS:
      return MiaEndpoints.FLASH_CARD_DECKS;
    case MiaFeatureType.QBANK:
      return MiaEndpoints.QUESTION_BANKS_FROM_CONTENT;
    case MiaFeatureType.PROBLEM_SOLVING:
      return MiaEndpoints.PROBLEMS;
    default:
      throw new Error(`Unknown feature type: ${featureType}`);
  }
}
